import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Users, FileText, AlertCircle, TrendingUp, Clock, CheckCircle2, DollarSign, Shield, Search, CalendarClock, BarChart3, CalendarCheck } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { formatDate } from "@/utils/dates";

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-outfit font-bold text-slate-900 dark:text-slate-100">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const { axiosAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkDate, setCheckDate] = useState("");
  const [checkTime, setCheckTime] = useState("12:00");
  const [availResult, setAvailResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [profitability, setProfitability] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [statsRes, profitRes] = await Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/profitability")]);
      setStats(statsRes.data);
      setProfitability(profitRes.data);
    } catch (err) { console.error("Error fetching stats:", err); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const checkAvailability = async () => {
    if (!checkDate) return;
    setChecking(true);
    try {
      const api = axiosAuth();
      const res = await api.get(`/vehicles/check-availability?date=${checkDate}&time=${checkTime}`);
      setAvailResult(res.data);
    } catch (err) { console.error(err); }
    finally { setChecking(false); }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-xl animate-pulse">
            <CardContent className="p-5 h-24" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Grid - 5 columns with prenotazioni */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Veicoli Totali" value={stats?.total_vehicles || 0} subtitle={`${stats?.available_vehicles || 0} disponibili`} icon={Car} color="bg-slate-900 dark:bg-slate-700" />
        <StatCard title="Noleggi Attivi" value={stats?.active_rentals || 0} subtitle={`${stats?.total_rentals || 0} totali`} icon={FileText} color="bg-red-600" />
        <StatCard title="Prenotazioni" value={stats?.prenotazioni || 0} subtitle="In attesa" icon={CalendarCheck} color="bg-indigo-600" />
        <StatCard title="Clienti" value={stats?.total_clients || 0} subtitle="Registrati" icon={Users} color="bg-emerald-600" />
        <StatCard title="Ricavi Totali" value={`${(stats?.total_revenue || 0).toLocaleString("it-IT")} EUR`} subtitle={`${(stats?.total_deposits || 0).toLocaleString("it-IT")} EUR in acconti`} icon={DollarSign} color="bg-amber-500" />
      </div>

      {/* Quick Availability Check */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl" data-testid="availability-check-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-red-600 dark:text-red-400" /> Controllo Rapido Disponibilita
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <div className="space-y-1 flex-1 w-full sm:w-auto">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Data</Label>
              <Input data-testid="avail-check-date" type="date" value={checkDate} onChange={(e) => { setCheckDate(e.target.value); setAvailResult(null); }} className="h-10 rounded-lg" />
            </div>
            <div className="space-y-1 w-full sm:w-32">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Ora</Label>
              <Input data-testid="avail-check-time" type="time" value={checkTime} onChange={(e) => { setCheckTime(e.target.value); setAvailResult(null); }} className="h-10 rounded-lg" />
            </div>
            <Button data-testid="avail-check-button" onClick={checkAvailability} disabled={!checkDate || checking} className="bg-red-600 hover:bg-red-700 text-white rounded-lg h-10 px-5 w-full sm:w-auto">
              <Search className="w-4 h-4 mr-2" /> {checking ? "Verifica..." : "Verifica"}
            </Button>
          </div>

          {availResult && (
            <div className="mt-4 space-y-3" data-testid="avail-check-results">
              <div className="flex gap-3">
                <div className="flex-1 p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg border border-emerald-200 dark:border-emerald-800 text-center">
                  <p className="text-2xl font-outfit font-bold text-emerald-700 dark:text-emerald-400" data-testid="avail-count">{availResult.available_count}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500">Disponibili</p>
                </div>
                <div className="flex-1 p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-800 text-center">
                  <p className="text-2xl font-outfit font-bold text-red-700 dark:text-red-400" data-testid="busy-count">{availResult.busy_count}</p>
                  <p className="text-xs text-red-600 dark:text-red-500">Occupati</p>
                </div>
                <div className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-center">
                  <p className="text-2xl font-outfit font-bold text-slate-700 dark:text-slate-200">{availResult.total}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Totale Flotta</p>
                </div>
              </div>

              {availResult.available.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1.5 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Veicoli Liberi
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {availResult.available.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 p-2 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-lg border border-emerald-100 dark:border-emerald-800" data-testid={`avail-vehicle-${v.id}`}>
                        <Car className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{v.brand} {v.model}</p>
                          <p className="text-[11px] font-mono text-slate-400 dark:text-slate-500">{v.plate} - {v.daily_rate} EUR/g</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {availResult.busy.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Veicoli Occupati
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {availResult.busy.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 p-2 bg-red-50/50 dark:bg-red-950/20 rounded-lg border border-red-100 dark:border-red-800" data-testid={`busy-vehicle-${v.id}`}>
                        <Car className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{v.brand} {v.model}</p>
                          <p className="text-[11px] font-mono text-slate-400 dark:text-slate-500">{v.plate}</p>
                          {v.rental_info && (
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                              {v.rental_info.client_name} - rientro {formatDate(v.rental_info.end_date)} {v.rental_info.end_time}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {availResult.available_count === 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 text-center">
                  <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">Nessun veicolo disponibile in questa data/ora</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chart + Payment Status */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-8 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-600" /> Andamento Ricavi
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {stats?.chart_data && stats.chart_data.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={stats.chart_data}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC2626" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="[&>line]:stroke-slate-200 dark:[&>line]:stroke-slate-700" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} className="[&_.recharts-text]:fill-slate-500 dark:[&_.recharts-text]:fill-slate-400" />
                  <YAxis tick={{ fontSize: 12 }} className="[&_.recharts-text]:fill-slate-500 dark:[&_.recharts-text]:fill-slate-400" />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid var(--border-color, #e2e8f0)", fontSize: "13px", backgroundColor: "var(--tooltip-bg, #fff)", color: "var(--tooltip-text, #334155)" }} formatter={(value) => [`${value.toLocaleString("it-IT")} EUR`, "Ricavi"]} />
                  <Area type="monotone" dataKey="revenue" stroke="#DC2626" strokeWidth={2} fill="url(#revenueGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
                Nessun dato disponibile. Aggiungi noleggi per visualizzare il grafico.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100">Stato Pagamenti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" /><span className="text-sm font-medium text-red-700 dark:text-red-300">Da Pagare</span></div>
              <span className="text-lg font-bold text-red-700 dark:text-red-300">{stats?.da_pagare_count || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" /><span className="text-sm font-medium text-amber-700 dark:text-amber-300">Acconto</span></div>
              <span className="text-lg font-bold text-amber-700 dark:text-amber-300">{stats?.acconto_count || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /><span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Pagato</span></div>
              <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{stats?.pagato_count || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue rentals */}
      {stats?.overdue_rentals && stats.overdue_rentals.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl" data-testid="overdue-rentals-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" /> Contratti Scaduti
              <Badge className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 text-xs ml-1">{stats.overdue_rentals.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.overdue_rentals.map((r) => {
                const daysOver = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(r.end_date?.slice(0, 10)).setHours(0, 0, 0, 0)) / 86400000);
                return (
                  <div key={r.id} className="flex items-center justify-between p-3 bg-red-50/60 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {r.vehicle?.brand} {r.vehicle?.model}
                        <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">{r.vehicle?.plate}</span>
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{r.client?.name} {r.client?.surname} - Scaduto il: {formatDate(r.end_date)}</p>
                    </div>
                    <Badge className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 text-xs">{daysOver} {daysOver === 1 ? "giorno" : "giorni"} fa</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Soon */}
      {stats?.expiring_soon && stats.expiring_soon.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl" data-testid="expiring-soon-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" /> Noleggi in Scadenza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.expiring_soon.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {r.vehicle?.brand} {r.vehicle?.model}
                      <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">{r.vehicle?.plate}</span>
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{r.client?.name} {r.client?.surname} - Scade: {formatDate(r.end_date)}</p>
                  </div>
                  <Badge className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700 text-xs">In Scadenza</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insurance */}
      {stats?.insurance_expiring && stats.insurance_expiring.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl" data-testid="insurance-expiring-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-500" /> Assicurazioni in Scadenza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.insurance_expiring.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-red-50/50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {r.vehicle?.brand} {r.vehicle?.model}
                      <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">{r.vehicle?.plate}</span>
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{r.insurance_company ? `${r.insurance_company} - ` : ""}Scade: {formatDate(r.expiry_date)}</p>
                  </div>
                  <Badge className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 text-xs border">Assicurazione</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profitability */}
      {profitability.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl" data-testid="profitability-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" /> Analisi Redditivita per Veicolo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={profitability.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="[&>line]:stroke-slate-200 dark:[&>line]:stroke-slate-700" />
                  <XAxis type="number" tick={{ fontSize: 11 }} className="[&_.recharts-text]:fill-slate-500 dark:[&_.recharts-text]:fill-slate-400" />
                  <YAxis type="category" dataKey="plate" tick={{ fontSize: 11 }} width={80} className="[&_.recharts-text]:fill-slate-500 dark:[&_.recharts-text]:fill-slate-400" />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid var(--border-color, #e2e8f0)", fontSize: "12px", backgroundColor: "var(--tooltip-bg, #fff)", color: "var(--tooltip-text, #334155)" }}
                    formatter={(value, name) => {
                      const labels = { net_profit: "Profitto Netto", total_revenue: "Ricavi", maintenance_cost: "Costi Manut." };
                      return [`${Number(value).toLocaleString("it-IT")} EUR`, labels[name] || name];
                    }}
                  />
                  <Bar dataKey="total_revenue" fill="#10b981" radius={[0, 4, 4, 0]} name="total_revenue" />
                  <Bar dataKey="maintenance_cost" fill="#f59e0b" radius={[0, 4, 4, 0]} name="maintenance_cost" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Veicolo</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Noleggi</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Giorni</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Ricavi</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Manut.</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Profitto</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">Media/G</th>
                  </tr>
                </thead>
                <tbody>
                  {profitability.map((v) => (
                    <tr key={v.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`profit-row-${v.id}`}>
                      <td className="py-2 px-2">
                        <span className="font-medium text-slate-900 dark:text-slate-200">{v.brand} {v.model}</span>
                        <span className="ml-1 text-[11px] font-mono text-slate-400">{v.plate}</span>
                      </td>
                      <td className="text-right py-2 px-2 text-slate-600 dark:text-slate-400">{v.rental_count}</td>
                      <td className="text-right py-2 px-2 text-slate-600 dark:text-slate-400">{v.total_days}</td>
                      <td className="text-right py-2 px-2 font-medium text-emerald-600 dark:text-emerald-400">{v.total_revenue.toLocaleString("it-IT")} EUR</td>
                      <td className="text-right py-2 px-2 text-amber-600 dark:text-amber-400">{v.maintenance_cost.toLocaleString("it-IT")} EUR</td>
                      <td className="text-right py-2 px-2">
                        <span className={`font-bold ${v.net_profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                          {v.net_profit.toLocaleString("it-IT")} EUR
                        </span>
                      </td>
                      <td className="text-right py-2 px-2 text-slate-500 dark:text-slate-400">{v.avg_daily_revenue} EUR</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
